import React from "react";
import SwingTimeline from '../sentiment/SwingTimeline';
import { formatCodexMarkerTime } from "../codex/codexEvaluationView";
import CodexReferenceSummary, { hasStructuredReferenceAssessment } from "./CodexReferenceSummary";

const label = (value) => String(value || "")
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replaceAll("_", " ")
  .toLowerCase()
  .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
const value = (input) => input === null || input === undefined || input === "" ? "n/a" : String(input);
const score = (input) => input === null || input === undefined || input === ""
  ? "Score not available" : `Score ${input}/100`;
const researchTime = (input) => Number.isFinite(Date.parse(input))
  ? new Date(input).toLocaleString() : "Time unavailable";

function Thesis({ title, thesis }) {
  if (!thesis) return null;
  return (
    <section className="codexReportSection">
      <h5>{title}</h5>
      <p>{thesis.summary}</p>
      <dl className="codexReportDefinitionList">
        <dt>Confirmation</dt><dd>{thesis.confirmation}</dd>
        <dt>Invalidation</dt><dd>{thesis.invalidation}</dd>
        <dt>Evidence</dt><dd>{(thesis.evidenceRefs || []).join(", ") || "none"}</dd>
        {(thesis.missingEvidence || []).length > 0 && <><dt>Still missing</dt><dd>{thesis.missingEvidence.join("; ")}</dd></>}
      </dl>
    </section>
  );
}

function ScoreBreakdown({ breakdown = {} }) {
  return (
    <div className="codexScoreBreakdown">
      {Object.entries(breakdown).map(([key, component]) => (
        <div key={key} className="codexScoreRow">
          <strong>{label(key)} · {component.points}/{component.maximum}</strong>
          <span>{component.rationale}</span>
          <small>{(component.evidenceRefs || []).join(", ")}</small>
        </div>
      ))}
    </div>
  );
}

function TradePlan({ title, plan }) {
  if (!plan) return null;
  return (
    <section className="codexReportSection codexTradePlan">
      <h4>{title} · {plan.bias} · {plan.status} · {plan.score}/100</h4>
      <p>{plan.setup}</p>
      <dl className="codexReportDefinitionList">
        <dt>Thesis</dt><dd>{label(plan.thesis)}</dd>
        <dt>Holding period</dt><dd>{value(plan.holdingPeriod)}</dd>
        <dt>Entry zone</dt><dd>{value(plan.entryZone?.low)} – {value(plan.entryZone?.high)} (reference {value(plan.entryPrice)})</dd>
        <dt>Confirmation</dt><dd>{value(plan.confirmation)}</dd>
        <dt>Stop</dt><dd>{value(plan.stopLoss)} · risk/unit {value(plan.riskPerUnit)}</dd>
        <dt>Invalidation</dt><dd>{value(plan.invalidation)}</dd>
        <dt>Account risk</dt><dd>{plan.accountRiskPct == null ? "n/a" : `${plan.accountRiskPct}% maximum`}</dd>
        <dt>Evidence</dt><dd>{(plan.evidenceRefs || []).join(", ") || "none"}</dd>
      </dl>
      {(plan.targets || []).length > 0 && (
        <div className="codexTargets">
          {(plan.targets || []).map((target) => (
            <div key={`${target.label}-${target.price}`}>
              <strong>{target.label} · {value(target.price)} · {value(target.riskReward)}R</strong>
              <span>{target.rationale}</span>
            </div>
          ))}
        </div>
      )}
      <ScoreBreakdown breakdown={plan.scoreBreakdown} />
      {(plan.noTradeConditions || []).length > 0 && <p><strong>No trade:</strong> {plan.noTradeConditions.join("; ")}</p>}
    </section>
  );
}

function CompactTradePlan({ title, plan }) {
  if (!plan) return null;
  return (
    <section className="codexReportSection codexTradePlan">
      <h4>{title} · {plan.side} · {plan.timing}</h4>
      <p>{plan.rationale}</p>
      <div className="small">
        Entry {value(plan.entry)} · Stop {value(plan.stopLoss)} · TP1 {value(plan.takeProfit1)} · TP2 {value(plan.takeProfit2)}
      </div>
    </section>
  );
}

function ReferenceItems({ title, items = [] }) {
  if (!items.length) return null;
  return (
    <section className="codexReportSection">
      <h4>{title}</h4>
      <ol className="codexEvidenceList">
        {items.map((item, index) => {
          if (typeof item !== "object" || item === null) return <li key={index}>{String(item)}</li>;
          const heading = item.label || item.name || item.symbol || item.timeframe || `Item ${index + 1}`;
          const description = item.summary || item.observation || item.description || item.result;
          const facts = Object.entries(item)
            .filter(([key, entry]) => !["label", "name", "symbol", "timeframe", "summary", "observation", "description", "result"].includes(key)
              && ["string", "number", "boolean"].includes(typeof entry))
            .map(([key, entry]) => `${label(key)}: ${entry}`);
          return <li key={item.id || `${heading}-${index}`}>
            <strong>{heading}</strong>
            {description && <span>{description}</span>}
            {facts.length > 0 && <small>{facts.join(" · ")}</small>}
          </li>;
        })}
      </ol>
    </section>
  );
}

export default function CodexEvaluationReport({ evaluation }) {
  if (!evaluation) return null;
  const details = evaluation.details || {};
  const sharedRecord = evaluation._sharedRecord;
  const responseMode = String(evaluation.responseMode || "").toUpperCase();
  if (["LIGHT_V1", "LIGHT", "REFERENCE_V1"].includes(responseMode)) {
    const adjustedConfidence = Math.max(0, Math.min(100,
      Number(evaluation.adjustedConfidence ?? evaluation.confidence) || 0));
    const technicalConfidence = Number.isFinite(Number(evaluation.technicalConfidence))
      ? Math.max(0, Math.min(100, Number(evaluation.technicalConfidence))) : null;
    const sentiment = evaluation._sentimentContext;
    const weeklySentiment = sentiment?.latest?.horizonDays === 7;
    const swing = sentiment?.latest?.swing;
    return (
      <article className={`codexEvaluationReport codexEvaluationReportLight codexLight${evaluation.eventAlignment || "NEUTRAL"}`}>
        <header className="codexLightReportHeader">
          <div className="codexLightReportTitleRow">
            <div>
              <span className="codexLightEyebrow">Automatic assessment</span>
              <h3>{responseMode === "REFERENCE_V1" ? "AI reference comparison" : "AI event validation"}</h3>
            </div>
            <strong className="codexLightAlignment">{evaluation.eventAlignment}</strong>
          </div>

          <div className="codexLightMetrics">
            <div className="codexLightMetric">
              <span>Trend</span>
              <strong>{label(evaluation.trend)}</strong>
            </div>
            <div className="codexLightMetric">
              <span>Structure</span>
              <strong>{label(evaluation.structure)}</strong>
            </div>
          </div>

          <div className="codexLightConfidencePanel">
            <div className="codexLightConfidenceRow">
              <span>{technicalConfidence == null ? "Confidence" : "Adjusted confidence"}</span>
              <strong>{adjustedConfidence}%</strong>
            </div>
            <div
              className="codexLightConfidenceTrack"
              role="meter"
              aria-label={technicalConfidence == null ? "Confidence" : "Adjusted confidence"}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={adjustedConfidence}
            >
              <span style={{ width: `${adjustedConfidence}%` }} />
            </div>
            {technicalConfidence != null && <div className="codexLightTechnicalConfidence">
              Technical confidence {technicalConfidence}%
            </div>}
            {evaluation.evaluatedAt && (
              <time className="codexLightEvaluatedAt">
                {formatCodexMarkerTime(evaluation.evaluatedAt)} UTC
              </time>
            )}
          </div>
        </header>

        {(evaluation.sentimentImpact || sentiment) && <section className="codexSentimentContext">
          <div className="codexSentimentContextHeading">
            <h4>{weeklySentiment ? "Global forecast — next 7 days from report time" : "Global crypto-market context"}</h4>
            <strong>{sentiment?.latest?.verdict || "Report unavailable"}
              {evaluation.sentimentImpact ? ` · ${label(evaluation.sentimentImpact)}` : ""}</strong>
          </div>
          {sentiment?.latest?.summary && <p>{sentiment.latest.summary}</p>}
          {weeklySentiment && <p className="small">Expectations ahead, not performance over the previous seven days.</p>}
          <dl className="codexReportDefinitionList">
            {sentiment?.latest?.outlook && <><dt>Outlook</dt><dd>{weeklySentiment ? 'Next 7 days from report time' : sentiment.latest.outlook}</dd></>}
            {weeklySentiment && <><dt>Forecast window</dt><dd>{researchTime(sentiment.latest.asOf)} → {researchTime(sentiment.latest.outlookThrough)}</dd></>}
            {sentiment?.latest?.reviewType && <><dt>Research review</dt><dd>{sentiment.latest.reviewType === "DAILY_BASELINE" ? "Daily review" : "Hourly change check"}</dd></>}
            {swing && <>
              <dt>Current swing</dt><dd>{label(swing.direction)} · {label(swing.state)}. {swing.summary}</dd>
              <dt>Swing confirmation</dt><dd>{swing.confirmation}</dd>
              <dt>Swing invalidation</dt><dd>{swing.invalidation}</dd>
            </>}
            {sentiment?.latest?.invalidation && <><dt>Forecast invalidation</dt><dd>{sentiment.latest.invalidation}</dd></>}
            {sentiment?.weeklyBackground && <>
              <dt>Daily background — next 7 days</dt><dd>{sentiment.weeklyBackground.verdict}. {sentiment.weeklyBackground.summary}<small> · Research as of {researchTime(sentiment.weeklyBackground.asOf)} · {sentiment.weeklyBackground.reportAgeMinutes} minutes old when captured</small></dd>
              <dt>Daily background invalidation</dt><dd>{sentiment.weeklyBackground.invalidation}</dd>
            </>}
            {sentiment?.sequenceText && <><dt>{sentiment.sequenceEndsAt ? "24 hours ending at report" : "Previous 24 hours"}</dt><dd>{sentiment.sequenceText}{sentiment.sequenceEndsAt && <small> · Same outlook horizon; last hour starts {researchTime(sentiment.sequenceEndsAt)}</small>}</dd></>}
            {Number.isFinite(sentiment?.reportAgeMinutes) && <><dt>Report age at capture</dt><dd>{sentiment.reportAgeMinutes} minutes{sentiment.elapsedHoursSinceLatestReport > 0 ? ` · ${sentiment.elapsedHoursSinceLatestReport} full hour${sentiment.elapsedHoursSinceLatestReport === 1 ? '' : 's'} since the latest report` : ''}</dd></>}
            {evaluation.sentimentAdjustmentReason && <><dt>Confidence adjustment</dt><dd>{evaluation.sentimentAdjustmentReason}</dd></>}
            {sentiment?.latest?.asOf && <><dt>Report time</dt><dd>{researchTime(sentiment.latest.asOf)}</dd></>}
          </dl>
          {swing && <SwingTimeline swing={swing} asOf={sentiment.latest.asOf} intraday={!weeklySentiment}/>}
          {sentiment && <p className="small">Research context was saved with this assessment. UNKNOWN denotes unavailable evidence; swing states are qualitative. Technical confidence remains separate from the global adjustment.</p>}
        </section>}

        <CodexReferenceSummary evaluation={evaluation} detailed />
        {!hasStructuredReferenceAssessment(evaluation) && <section className="codexLightEvidencePanel">
          <h4>Key evidence</h4>
          <ul>
            {(evaluation.evidence || []).map((item, index) => <li key={index}>{item}</li>)}
          </ul>
        </section>}
      </article>
    );
  }
  if (["CHART_COMPACT", "COMPACT"].includes(responseMode)) {
    return (
      <article className="codexEvaluationReport codexEvaluationReportCompact">
        <header className="codexReportHeader">
          {sharedRecord && <strong>Codex · shared symbol evaluation</strong>}
          <h3>{value(evaluation.symbol || sharedRecord?.symbol)} · {evaluation.verdict} · {score(evaluation.score)}</h3>
          <p>{evaluation.summary}</p>
        </header>
        <section className="codexReportSection">
          <h4>Critical evidence</h4>
          <ol className="codexEvidenceList">
            {(evaluation.evidence || []).map((item, index) => <li key={index}>{item}</li>)}
          </ol>
        </section>
        {(evaluation.missingData || []).length > 0 && <section className="codexReportSection">
          <h5>Would improve assessment</h5>
          <ul>{evaluation.missingData.map((item, index) => <li key={index}>{item}</li>)}</ul>
        </section>}
        <CompactTradePlan title="Short-term" plan={evaluation.shortTerm} />
        <CompactTradePlan title="Swing" plan={evaluation.swing} />
      </article>
    );
  }
  if (["REFERENCE_V1", "REFERENCE"].includes(responseMode)) {
    return (
      <article className="codexEvaluationReport codexEvaluationReportReference">
        <header className="codexReportHeader">
          {sharedRecord && <strong>{sharedRecord.provider === "CUSTOM_AI" ? "Custom AI" : "Codex"} · reference assessment</strong>}
          <h3>{value(evaluation.symbol || sharedRecord?.symbol)} · {value(evaluation.verdict)}</h3>
          <div>
            {value(evaluation.side)} · confidence {value(evaluation.confidence)}%
            {evaluation.evaluatedAt ? ` · ${formatCodexMarkerTime(evaluation.evaluatedAt)} UTC` : ""}
          </div>
          {evaluation.summary && <p>{evaluation.summary}</p>}
        </header>
        <ReferenceItems title="Reference comparisons" items={evaluation.comparisons} />
        <ReferenceItems title="Evidence" items={evaluation.evidence} />
      </article>
    );
  }
  return (
    <article className="codexEvaluationReport">
      <header className="codexReportHeader">
        {sharedRecord && <strong>{sharedRecord.provider === "CUSTOM_AI" ? "Custom AI" : "Codex"} · shared symbol evaluation</strong>}
        <h3>{value(details.symbol || evaluation.symbol || sharedRecord?.symbol)} · {evaluation.verdict} · {score(evaluation.score)}</h3>
        <div>{evaluation.timing} · confidence {value(evaluation.confidence)}% · {formatCodexMarkerTime(evaluation.evaluatedAt)} UTC</div>
        {details.marketState && <strong>Market state: {label(details.marketState)}</strong>}
        <p>{evaluation.summary}</p>
      </header>

      {!details.intradayPlan && <section className="codexReportSection">
        <h4>Trade levels</h4>
        <dl className="codexReportDefinitionList">
          <dt>Side</dt><dd>{value(evaluation.side)}</dd>
          <dt>Entry</dt><dd>{value(evaluation.entry)}</dd>
          <dt>Stop</dt><dd>{value(evaluation.stopLoss)}</dd>
          <dt>TP1</dt><dd>{value(evaluation.takeProfit1)}</dd>
          <dt>TP2</dt><dd>{value(evaluation.takeProfit2)}</dd>
          <dt>Strengths</dt><dd>{(evaluation.strengths || []).join("; ") || "none"}</dd>
          <dt>Risks</dt><dd>{(evaluation.risks || []).join("; ") || "none"}</dd>
        </dl>
      </section>}

      {(sharedRecord?.eventContext || []).length > 0 && <section className="codexReportSection">
        <h4>Insights events supplied</h4>
        <ol className="codexEvidenceList">
          {sharedRecord.eventContext.map((event) => <li key={event.id}>
            <strong>{event.name} · {event.timeframe || "n/a"} · {event.status || "n/a"}</strong>
            <span>{event.description || `${event.direction || "NEUTRAL"} context`}</span>
            <small>{event.id}</small>
          </li>)}
        </ol>
      </section>}

      {sharedRecord?.inputDiagnostics && <section className="codexReportSection">
        <h5>Input diagnostics</h5>
        <p>{sharedRecord.inputDiagnostics.eventContextCount || 0} events · {sharedRecord.inputDiagnostics.screenshots || 0} screenshots</p>
      </section>}

      {(details.evidence || []).length > 0 && <section className="codexReportSection">
        <h4>Enumerated evidence</h4>
        <ol className="codexEvidenceList">
          {(details.evidence || []).map((item) => (
            <li key={item.id} className={`codexEvidence${item.impact}`}>
              <strong>{item.id} · {item.timeframe} · {label(item.metric)} · {item.value}</strong>
              <span>{item.observation}</span>
              <small>{item.impact} · {item.source} · {item.observedAt}</small>
            </li>
          ))}
        </ol>
      </section>}

      <div className="codexThesesGrid">
        <Thesis title="Continuation thesis" thesis={details.continuationThesis} />
        <Thesis title="Reversal thesis" thesis={details.reversalThesis} />
      </div>

      <TradePlan title="Intraday" plan={details.intradayPlan} />
      <TradePlan title="Swing" plan={details.swingPlan} />

      {(details.conflicts || []).length > 0 && <section className="codexReportSection"><h5>Conflicts</h5><ul>{details.conflicts.map((item, index) => <li key={index}>{item}</li>)}</ul></section>}
      {(details.limitations || []).length > 0 && <section className="codexReportSection"><h5>Limitations</h5><ul>{details.limitations.map((item, index) => <li key={index}>{item}</li>)}</ul></section>}
      {(details.toolsUsed || []).length > 0 && <section className="codexReportSection"><h5>Sources used</h5><p>{details.toolsUsed.join(", ")}</p></section>}
    </article>
  );
}
